import React from 'react'
import { ArrowUpRight } from 'react-feather'
import styled, { useTheme } from 'styled-components'
import { Flex, Text, TextVariant, CopyToClipboardButton, InfoIcon } from '@mprolab/uikit'
import { useThemeMediaQuery } from 'hooks/useThemeMediaQuery'
import { useMproTokenStats } from 'hooks/useMproTokenStats'
import {
  MAX_SUPPLY,
  MARKET_LINKS,
  addressUrl,
  chainName,
  formatTokens,
  shortAddress
} from 'utils/mproToken/tokenFacts'

const Card = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: 28px;
  padding: 28px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.backgroundContrast};
`

const Header = styled(Flex)`
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`

const Logo = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 50%;
`

const Badge = styled(Flex)`
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  color: ${({ theme }) => theme.colors.gray5};
  font-size: 12px;
  white-space: nowrap;
`

const MarketLink = styled.a`
  color: ${({ theme }) => theme.colors.purple};
  font-size: 14px;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`

const Section = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: 12px;
`

const StatGrid = styled.div<{ columns: number }>`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(${({ columns }) => columns}, minmax(0, 1fr));
  gap: 16px;
`

const Stat = styled(Flex)`
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.backgroundActive};
`

/**
 * Stands in for a figure while the chain is still answering. A dash is what we
 * show when a value is genuinely unavailable, so using it for "not yet" told
 * the reader the wrong thing — and told it in the same breath for both cases.
 */
const Shimmer = styled.div<{ width: string; height: string }>`
  width: ${({ width }) => width};
  height: ${({ height }) => height};
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.gray9} 25%,
    ${({ theme }) => theme.colors.gray8} 50%,
    ${({ theme }) => theme.colors.gray9} 75%
  );
  background-size: 200% 100%;
  animation: tokenCardShimmer 1.4s ease-in-out infinite;

  @keyframes tokenCardShimmer {
    from {
      background-position: 200% 0;
    }
    to {
      background-position: -200% 0;
    }
  }
`

const ChainRow = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.backgroundActive};
  flex-wrap: wrap;
`

const AddressLink = styled.a`
  color: ${({ theme }) => theme.colors.gray3};
  font-family: monospace;
  font-size: 14px;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colors.purple};
  }
`

/**
 * Said next to every figure the MPRO Lab community can change, rather than once
 * at the bottom of the card: a holder reading "5%" needs to know at that moment
 * that it is a decision, not a property of the token.
 */
const GOVERNED_NOTE =
  'This is a parameter, not a constant — the MPRO Lab community can change it by vote in the DAO.'

const Governed: React.FC = () => (
  <InfoIcon iconProps={{ size: 14 }} tooltipProps={{ title: GOVERNED_NOTE }} />
)

interface StatItemProps {
  label: string
  value: string
  hint?: string
  governed?: boolean
  loading?: boolean
}

const StatItem: React.FC<StatItemProps> = ({ label, value, hint, governed, loading }) => {
  const { colors } = useTheme()
  return (
    <Stat>
      <Flex alignItems="center" gap="6px">
        <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
          {label}
        </Text>
        {/* One icon per figure. Two side by side read as a rendering bug, and
            the reader cannot tell which explains what. */}
        {(hint || governed) && (
          <InfoIcon
            iconProps={{ size: 14 }}
            tooltipProps={{
              title: [hint, governed ? GOVERNED_NOTE : null].filter(Boolean).join(' ')
            }}
          />
        )}
      </Flex>
      {loading ? (
        <Shimmer width="70%" height="24px" />
      ) : (
        <Text variant={TextVariant.BODY_BIG_BOLD}>{value}</Text>
      )}
    </Stat>
  )
}

/**
 * The token's own page — the thing a service built to manage a token, its
 * distribution and its nodes was missing entirely. Until now the MPRO tab held
 * a tokenomics picture: no address to verify, no supply, no emission, and no
 * mention that transfers burn or that the distribution can be leveraged.
 *
 * Every figure here is read live from chain, so a holder can check any of it on
 * an explorer. Where a number is a decision rather than a fact, it is marked as
 * one the community votes on.
 */
const TokenCard: React.FC = () => {
  const { colors } = useTheme()
  const isMobile = useThemeMediaQuery('mobile')
  const isTablet = useThemeMediaQuery('tablet')
  const stats = useMproTokenStats()

  const columns = isMobile ? 1 : isTablet ? 2 : 4
  const burnedShare =
    stats.burned !== null && stats.everMinted ? (stats.burned / stats.everMinted) * 100 : null
  const mintingChain = stats.chains.find((c) => c.mints)

  return (
    <Card>
      <Header>
        <Logo src="/images/tokens/mpro.png" alt="MPRO" />
        <Flex flexDirection="column" gap="2px">
          <Text variant={TextVariant.H5}>MPRO Lab</Text>
          <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
            MPRO · 18 decimals
          </Text>
        </Flex>
        <Flex gap="8px" flexWrap="wrap" alignItems="center">
          <Badge>
            OFT — bridgeable
            <InfoIcon
              iconProps={{ size: 14, style: { marginLeft: 6 } }}
              tooltipProps={{
                title:
                  'MPRO is a LayerZero Omnichain Fungible Token. Moving it between chains burns it on the chain it leaves and mints it on the chain it arrives, so the total across all chains never changes. Use the Bridge tab.'
              }}
            />
          </Badge>
          {stats.chains.map((c) => (
            <Badge key={c.chainId}>{chainName(c.chainId)}</Badge>
          ))}
        </Flex>
        <Flex gap="12px" alignItems="center" style={{ marginLeft: 'auto' }}>
          {MARKET_LINKS.map((link) => (
            <MarketLink key={link.label} href={link.href} target="_blank" rel="noreferrer">
              {/* The drawn arrow, not the typographic one: it takes the
                  theme's colour and the same stroke weight as every other
                  icon in the portal. */}
              {link.label} <ArrowUpRight size={13} />
            </MarketLink>
          ))}
        </Flex>
      </Header>

      <>
          <Section>
            <Text variant={TextVariant.BODY_SMALL_BOLD} color={colors.gray5}>
              Supply
            </Text>
            <StatGrid columns={columns}>
              <StatItem
                label="Maximum supply"
                value={`${formatTokens(MAX_SUPPLY)} MPRO`}
                hint="A hard cap written into the token contract. It cannot be raised — not by the team, and not by a vote."
              />
              <StatItem
                label="Circulating"
                value={`${formatTokens(stats.circulating)} MPRO`}
                hint="Every MPRO that exists right now, added up across all chains. Bridging moves supply between chains without changing this number."
              loading={stats.loading}
              />
              <StatItem
                label="Ever minted"
                value={`${formatTokens(stats.everMinted)} MPRO`}
                hint="The initial mint plus everything the distributor has created since. What is left of it, after burning, is the circulating supply."
              loading={stats.loading}
              />
              <StatItem
                label="Burned"
                value={
                  stats.burned === null
                    ? '—'
                    : `${formatTokens(stats.burned)} MPRO${
                        burnedShare === null ? '' : ` · ${burnedShare.toFixed(2)}%`
                      }`
                }
                hint="Destroyed for good. No contract counts burned tokens, so this is what the chain proves: everything ever minted, minus everything that still exists."
              loading={stats.loading}
              />
            </StatGrid>
          </Section>

          <Section>
            <Flex alignItems="center" gap="6px">
              <Text variant={TextVariant.BODY_SMALL_BOLD} color={colors.gray5}>
                Distribution
              </Text>
              <InfoIcon
                iconProps={{ size: 14 }}
                tooltipProps={{
                  title:
                    'New MPRO is created on a daily schedule and shared between licences by how long their nodes worked. It is minted when a holder claims, so the schedule runs ahead of the supply.'
                }}
              />
            </Flex>
            <StatGrid columns={columns}>
              <StatItem
                label="Unlocked to date"
                value={`${formatTokens(stats.unlocked)} MPRO`}
                hint="What the emission schedule has released since it started. Not all of it has been minted."
                governed
              loading={stats.loading}
              />
              <StatItem
                label="Minted from the schedule"
                value={`${formatTokens(stats.distributed)} MPRO`}
                hint="The part of the unlocked supply that holders have already claimed into existence."
              loading={stats.loading}
              />
              <StatItem
                label="Ready to mint"
                value={`${formatTokens(stats.readyToMint)} MPRO`}
                hint="Unlocked by the schedule and not yet minted — it is created as holders claim their share."
              loading={stats.loading}
              />
              <StatItem
                label="Emission started"
                value={
                  stats.distributionStart
                    ? stats.distributionStart.toISOString().slice(0, 10)
                    : '—'
                }
                hint="The day the daily distribution began."
              loading={stats.loading}
              />
            </StatGrid>
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
              Your share of a day&apos;s distribution is not fixed: it is leveraged by the licences
              in your referral structure, which add bonus credits on top of the credits your own
              nodes earn. The daily amount, the credits each licence type earns, the qualifying
              working time and the booster tiers are all parameters — the MPRO Lab community can
              change any of them by vote in the DAO.
            </Text>
          </Section>

          <Section>
            <Text variant={TextVariant.BODY_SMALL_BOLD} color={colors.gray5}>
              Contracts
            </Text>
            {mintingChain && (
              <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                New MPRO is minted on {chainName(mintingChain.chainId)}. The token&apos;s total
                supply is the sum of its supply on every chain: bridging burns the tokens on the
                chain they leave and the bridge mints them on the chain they arrive, so moving MPRO
                between chains changes each chain&apos;s figure but never the total.
              </Text>
            )}
            {stats.chains.map((chain) => (
              <ChainRow key={chain.chainId}>
                <Flex flexDirection="column" gap="2px">
                  <Flex alignItems="center" gap="8px">
                    <Text variant={TextVariant.BODY_SMALL_BOLD}>{chainName(chain.chainId)}</Text>
                    <Badge>{chain.mints ? 'mints' : 'bridged'}</Badge>
                  </Flex>
                  <Flex alignItems="center" gap="4px">
                    <AddressLink
                      href={addressUrl(chain.chainId, chain.address)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {isMobile ? shortAddress(chain.address) : chain.address}
                    </AddressLink>
                    <CopyToClipboardButton text={chain.address} />
                  </Flex>
                </Flex>
                <Flex gap="24px" flexWrap="wrap">
                  <Flex flexDirection="column">
                    <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                      Supply here
                    </Text>
                    {stats.loading ? (
                      <Shimmer width="90px" height="18px" />
                    ) : (
                      <Text variant={TextVariant.BODY_SMALL}>
                        {formatTokens(chain.supply)} MPRO
                      </Text>
                    )}
                  </Flex>
                  <Flex flexDirection="column">
                    <Flex alignItems="center" gap="4px">
                      <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                        Burn on transfer
                      </Text>
                      <Governed />
                    </Flex>
                    {stats.loading ? (
                      <Shimmer width="44px" height="18px" />
                    ) : (
                      <Text variant={TextVariant.BODY_SMALL}>
                        {chain.burnPercent === null ? '—' : `${chain.burnPercent}%`}
                      </Text>
                    )}
                  </Flex>
                </Flex>
              </ChainRow>
            ))}
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
              Sending MPRO burns a share of the amount, so the recipient gets less than was sent.
              The rate differs per chain and is shown above, live from the contract. A whitelisted
              address — the distributor and the platform&apos;s own contracts — pays no burn.
            </Text>
          </Section>
      </>
    </Card>
  )
}

export default TokenCard
